import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import WelcomeScreen from "@/components/WelcomeScreen";
import VisitorForm from "@/components/VisitorForm";
import PhotoCapture from "@/components/PhotoCapture";
import PersonalityQuestions from "@/components/PersonalityQuestions";
import AnalysisLoading from "@/components/AnalysisLoading";
import ResultsScreen from "@/components/ResultsScreen";
import LeadCapture from "@/components/LeadCapture";
import AdminDashboard from "@/components/AdminDashboard";
import LandingPage from "@/components/LandingPage";
import LoginModal from "@/components/LoginModal";
import { isAuthenticated } from "@/auth";
import { AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const SESSION_KEY = "heroscan_session";

function loadSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSession(data) {
  try {
    // Avatar URLs are large base64 strings — store result without avatar to avoid quota errors
    const toSave = {
      ...data,
      analysisResult: data.analysisResult
        ? { ...data.analysisResult, avatar_url: null }
        : null,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(toSave));
  } catch {
    // If storage quota exceeded, clear and let user restart
  }
}

function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

// Route guards — redirect to home if required data is missing
const Guard = ({ needs, children }) => {
  if (!needs) return <Navigate to="/" replace />;
  return children;
};

// Auth guard — shows login modal instead of redirecting
const RequireAuth = ({ children, onUnauthorized }) => {
  if (!isAuthenticated()) {
    onUnauthorized();
    return <Navigate to="/" replace />;
  }
  return children;
};

const HeroScanApp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const session = loadSession();
  const [visitorData, setVisitorData] = useState(session.visitorData || null);
  const [visitorId, setVisitorId] = useState(session.visitorId || null);
  const [questionAnswers, setQuestionAnswers] = useState(session.questionAnswers || null);
  const [analysisResult, setAnalysisResult] = useState(session.analysisResult || null);
  const [heroCount, setHeroCount] = useState(0);

  // Persist state to sessionStorage on every change
  useEffect(() => {
    saveSession({ visitorData, visitorId, questionAnswers, analysisResult });
  }, [visitorData, visitorId, questionAnswers, analysisResult]);

  useEffect(() => {
    axios.get(`${API}/hero-count`).then((r) => setHeroCount(r.data.count)).catch(() => {});
  }, []);

  const handleBeginScan = () => navigate("/app/form");

  const handleVisitorSubmit = (data) => {
    setVisitorData(data);
    navigate("/app/photo");
  };

  const handlePhotoCapture = async (photo) => {
    try {
      const response = await axios.post(`${API}/visitors`, { ...visitorData, photo_url: photo });
      setVisitorId(response.data.id);
      navigate("/app/questions");
    } catch (e) {
      console.error("Failed to create visitor:", e);
      toast.error("Failed to save your photo. Please try again.");
    }
  };

  const handleSkipPhoto = async () => {
    try {
      const response = await axios.post(`${API}/visitors`, visitorData);
      setVisitorId(response.data.id);
      navigate("/app/questions");
    } catch (e) {
      console.error("Failed to create visitor:", e);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleQuestionsComplete = (answers) => {
    setQuestionAnswers(answers);
    navigate("/app/analyzing");
  };

  const handleAnalysisComplete = (result) => {
    setAnalysisResult(result);
    setHeroCount((prev) => prev + 1);
    navigate("/app/results");
  };

  const handleResultsComplete = () => navigate("/app/lead");

  const handleLeadComplete = () => {
    clearSession();
    setVisitorData(null);
    setVisitorId(null);
    setQuestionAnswers(null);
    setAnalysisResult(null);
    navigate("/");
  };

  return (
    <div className="min-h-screen animated-gradient overflow-hidden" data-testid="heroscan-app">
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={<WelcomeScreen onBegin={handleBeginScan} heroCount={heroCount} />}
          />
          <Route
            path="form"
            element={
              <VisitorForm
                onSubmit={handleVisitorSubmit}
                onBack={() => navigate("/app")}
                savedData={visitorData}
              />
            }
          />
          <Route
            path="photo"
            element={
              <Guard needs={visitorData}>
                <PhotoCapture
                  onCapture={handlePhotoCapture}
                  onSkip={handleSkipPhoto}
                  onBack={() => navigate("/app/form")}
                />
              </Guard>
            }
          />
          <Route
            path="questions"
            element={
              <Guard needs={visitorId}>
                <PersonalityQuestions
                  onComplete={handleQuestionsComplete}
                  onBack={() => navigate("/app/photo")}
                  savedAnswers={questionAnswers}
                />
              </Guard>
            }
          />
          <Route
            path="analyzing"
            element={
              <Guard needs={visitorId && questionAnswers}>
                <AnalysisLoading
                  visitorId={visitorId}
                  visitorData={visitorData}
                  questionAnswers={questionAnswers}
                  onComplete={handleAnalysisComplete}
                />
              </Guard>
            }
          />
          <Route
            path="results"
            element={
              <Guard needs={analysisResult}>
                <ResultsScreen
                  result={analysisResult}
                  visitorData={visitorData}
                  onContinue={handleResultsComplete}
                />
              </Guard>
            }
          />
          <Route
            path="lead"
            element={
              <Guard needs={analysisResult && visitorId}>
                <LeadCapture
                  visitorId={visitorId}
                  result={analysisResult}
                  onComplete={handleLeadComplete}
                />
              </Guard>
            }
          />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </AnimatePresence>
      <Toaster position="top-center" />
    </div>
  );
};

function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);

  const requestAuth = (destination = "/app") => {
    if (isAuthenticated()) return destination;
    setPendingNav(destination);
    setShowLogin(true);
    return null;
  };

  return (
    <div className="App dark">
      <BrowserRouter>
        <LoginModal
          isOpen={showLogin}
          onSuccess={() => {
            setShowLogin(false);
            if (pendingNav) window.location.href = pendingNav;
          }}
          onClose={() => setShowLogin(false)}
        />
        <Routes>
          <Route path="/" element={<LandingPage requestAuth={requestAuth} />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route
            path="/app/*"
            element={
              <RequireAuth onUnauthorized={() => { setPendingNav("/app"); setShowLogin(true); }}>
                <HeroScanApp />
              </RequireAuth>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
